import { useEffect } from 'react';

/**
 * Seo — dependency-free per-route document head manager.
 *
 * CRA ships a single static index.html, so without this every route shares one
 * <title>/description. Googlebot renders our JS, so imperatively patching the
 * head per route gives each page its own title, description, canonical, social
 * cards, and JSON-LD. Everything is restored/removed on unmount so routes don't
 * leak tags into one another.
 *
 * Positioning note: copy here deliberately frames Stratos as an *achievement
 * recognition registry*, never a developmental screening or diagnostic service.
 * That keeps us out of Google's YMYL (Your Money or Your Life) child-health
 * bucket — see ResourceHub for the outbound E-E-A-T anchor that reinforces it.
 */

export const SITE_URL = 'https://stratosstandard.com';
export const SITE_NAME = 'Stratos Standard';
const DEFAULT_IMAGE = `${SITE_URL}/stratos-logo.png`;
const DEFAULT_DESCRIPTION =
  'Stratos Standard is an early-childhood achievement registry. We run warm, ' +
  'play-based verification sessions and record each milestone as a permanent, ' +
  'searchable registry entry for children ages 0–6.';

function upsertMeta(attr, key, content) {
  if (content == null) return null;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  let created = false;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
    created = true;
  }
  const prev = el.getAttribute('content');
  el.setAttribute('content', content);
  return { el, prev, created };
}

function upsertLink(rel, href) {
  if (!href) return null;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  let created = false;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
    created = true;
  }
  const prev = el.getAttribute('href');
  el.setAttribute('href', href);
  return { el, prev, created };
}

export default function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '/',
  image = DEFAULT_IMAGE,
  type = 'website',
  noindex = false,
  jsonLd = null,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | Early Achievement Registry`;
  const canonical = `${SITE_URL}${path === '/' ? '' : path}`;

  useEffect(() => {
    const prevTitle = document.title;
    document.title = fullTitle;

    const patches = [
      upsertMeta('name', 'description', description),
      upsertMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow'),
      upsertMeta('property', 'og:title', fullTitle),
      upsertMeta('property', 'og:description', description),
      upsertMeta('property', 'og:type', type),
      upsertMeta('property', 'og:url', canonical),
      upsertMeta('property', 'og:image', image),
      upsertMeta('property', 'og:site_name', SITE_NAME),
      upsertMeta('name', 'twitter:card', 'summary_large_image'),
      upsertMeta('name', 'twitter:title', fullTitle),
      upsertMeta('name', 'twitter:description', description),
      upsertMeta('name', 'twitter:image', image),
    ].filter(Boolean);

    const linkPatch = upsertLink('canonical', canonical);

    // JSON-LD: tag our scripts so we only ever remove our own.
    const blocks = (Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : []).map((data) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.setAttribute('data-seo', 'route');
      s.text = JSON.stringify(data);
      document.head.appendChild(s);
      return s;
    });

    return () => {
      document.title = prevTitle;
      // Restore meta we mutated; remove meta we created.
      patches.forEach((p) => {
        if (p.created) p.el.remove();
        else if (p.prev != null) p.el.setAttribute('content', p.prev);
      });
      if (linkPatch) {
        if (linkPatch.created) linkPatch.el.remove();
        else if (linkPatch.prev != null) linkPatch.el.setAttribute('href', linkPatch.prev);
      }
      blocks.forEach((s) => s.remove());
    };
  }, [fullTitle, description, canonical, image, type, noindex, jsonLd]);

  return null;
}
